import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ChipSelect from '@/components/chip-select'
import DataTable from '@/components/data-table'
import type { Column } from '@/components/data-table'
import DateRangePicker from '@/components/date-range-picker'
import FilterBar from '@/components/filter-bar'
import FormField from '@/components/form-field'
import { Input } from '@/components/input'
import PageHeader from '@/components/page-header'
import Select from '@/components/select'
import StatusBadge from '@/components/status-badge'
import { useGuiasQuery } from '@/http/queries/guias'
import { resolveSituacaoGuia, resolveTipoGuia, situacaoGuiaOptions, tipoGuiaOptions } from '@/types/domain'
import type { GuiaResumida } from '@/types/guia'
import { notifyWarning } from '@/utility/notify'
import { useDebouncedValue } from '@/utility/use-debounced-value'
import { formatDateDefault, orDash } from '@/utility/Utils'
import { contarFiltrosDoPainel, filtroVazio, montarChips, paraFiltroApi } from './filtro'
import type { FiltroGuias } from './filtro'
import styles from './style.module.css'

const AutorizacoesList = () => {
  const navigate = useNavigate()
  const { state } = useLocation() as { state?: { filtro?: FiltroGuias } }
  const filtroDaNavegacao = state?.filtro ?? filtroVazio

  const [busca, setBusca] = useState(filtroDaNavegacao.nomePaciente)
  const [aplicado, setAplicado] = useState<FiltroGuias>(filtroDaNavegacao)
  const [rascunho, setRascunho] = useState<FiltroGuias>(filtroDaNavegacao)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const buscaDebounced = useDebouncedValue(busca)

  useEffect(() => {
    setAplicado((atual) =>
      atual.nomePaciente === buscaDebounced ? atual : { ...atual, nomePaciente: buscaDebounced },
    )
    setPageNumber(1)
  }, [buscaDebounced])

  const filtro = useMemo(
    () => ({ ...paraFiltroApi(aplicado, pageSize), pageNumber }),
    [aplicado, pageNumber, pageSize],
  )

  const { data: page, error, isFetching, refetch } = useGuiasQuery(filtro)

  const efetivar = (proximo: FiltroGuias) => {
    setAplicado(proximo)
    setRascunho(proximo)
    setBusca(proximo.nomePaciente)
    setPageNumber(1)
  }

  const alterarRascunho = <C extends keyof FiltroGuias>(campo: C, valor: FiltroGuias[C]) => {
    setRascunho((atual) => ({ ...atual, [campo]: valor }))
  }

  const alternarSituacoes = (situacoes: string[]) => {
    alterarRascunho('situacoes', situacoes)
  }

  const columns: Column<GuiaResumida>[] = [
    {
      key: 'beneficiario',
      header: 'Beneficiário',
      render: (guia) => (
        <div className={styles.stack}>
          <strong>{orDash(guia.nomeBeneficiario)}</strong>
          <span className={styles.muted}>Carteira {orDash(guia.numeroCarteira)}</span>
        </div>
      ),
    },
    {
      key: 'operadora',
      header: 'Operadora',
      render: (guia) => (
        <div className={styles.stack}>
          <span>{orDash(guia.nomeOperadora)}</span>
          <span className={styles.muted}>{orDash(guia.nomeCredencial)}</span>
        </div>
      ),
    },
    {
      key: 'guia',
      header: 'Guia',
      render: (guia) => (
        <div className={styles.stack}>
          <span>{orDash(guia.guiaNaOperadora ?? guia.numeroSolicitacao)}</span>
          <span className={styles.muted}>{resolveTipoGuia(guia.tipoGuia, guia.nomeTipoGuia)}</span>
        </div>
      ),
    },
    { key: 'senha', header: 'Senha', render: (guia) => orDash(guia.senhaNaOperadora) },
    { key: 'emissao', header: 'Emissão', render: (guia) => formatDateDefault(guia.dataEmissao) },
    {
      key: 'autorizacao',
      header: 'Autorização',
      render: (guia) => formatDateDefault(guia.dataAutorizacao),
    },
    {
      key: 'situacao',
      header: 'Situação',
      render: (guia) => {
        const meta = resolveSituacaoGuia(guia.situacao, guia.nomeSituacao)
        return <StatusBadge label={meta.label} tone={meta.tone} />
      },
    },
  ]

  return (
    <>
      <PageHeader
        title="Autorizações"
        subtitle="Consulte, investigue e resolva autorizações travadas"
      />

      <FilterBar
        searchValue={busca}
        onSearchChange={setBusca}
        onSearchClear={() => efetivar({ ...aplicado, nomePaciente: '' })}
        searchPlaceholder="Buscar pelo nome do beneficiário"
        searchLabel="Buscar autorizações"
        isSearching={isFetching && busca !== buscaDebounced}
        chips={montarChips(aplicado, (campo) =>
          efetivar({ ...aplicado, [campo]: filtroVazio[campo] }),
        )}
        activeCount={contarFiltrosDoPainel(aplicado)}
        onApply={() => efetivar({ ...rascunho, nomePaciente: aplicado.nomePaciente })}
        onClear={() => efetivar(filtroVazio)}
        quickFilters={
          <ChipSelect
            options={situacaoGuiaOptions}
            value={rascunho.situacoes}
            onChange={alternarSituacoes}
            ariaLabel="Situação da autorização"
          />
        }
        advanced={
          <>
            <FormField label="Carteira" htmlFor="numeroCarteira">
              <Input
                id="numeroCarteira"
                placeholder="Nº da carteira"
                value={rascunho.numeroCarteira}
                onChange={(event) => alterarRascunho('numeroCarteira', event.target.value)}
              />
            </FormField>

            <FormField label="Nº guia prestador" htmlFor="numeroGuiaPrestador">
              <Input
                id="numeroGuiaPrestador"
                placeholder="Nº da guia no prestador"
                value={rascunho.numeroGuiaPrestador}
                onChange={(event) => alterarRascunho('numeroGuiaPrestador', event.target.value)}
              />
            </FormField>

            <FormField label="Nº guia operadora" htmlFor="numeroGuiaOperadora">
              <Input
                id="numeroGuiaOperadora"
                placeholder="Nº da guia na operadora"
                value={rascunho.numeroGuiaOperadora}
                onChange={(event) => alterarRascunho('numeroGuiaOperadora', event.target.value)}
              />
            </FormField>

            <FormField label="Senha da autorização" htmlFor="senhaAutorizacao">
              <Input
                id="senhaAutorizacao"
                placeholder="Senha da autorização"
                value={rascunho.senhaAutorizacao}
                onChange={(event) => alterarRascunho('senhaAutorizacao', event.target.value)}
              />
            </FormField>

            <FormField label="Tipo de guia" htmlFor="tipoGuia">
              <Select
                id="tipoGuia"
                options={tipoGuiaOptions}
                placeholder="Todos"
                value={rascunho.tipoGuia}
                onChange={(event) => alterarRascunho('tipoGuia', event.target.value)}
              />
            </FormField>

            <FormField label="Data de emissão" htmlFor="intervaloEmissao">
              <DateRangePicker
                id="intervaloEmissao"
                value={rascunho.intervaloEmissao}
                onChange={(datas) => alterarRascunho('intervaloEmissao', datas)}
                onRangeError={notifyWarning}
                maxDate="today"
              />
            </FormField>

            <FormField label="Data de autorização" htmlFor="intervaloAutorizacao">
              <DateRangePicker
                id="intervaloAutorizacao"
                value={rascunho.intervaloAutorizacao}
                onChange={(datas) => alterarRascunho('intervaloAutorizacao', datas)}
                onRangeError={notifyWarning}
                maxDate="today"
              />
            </FormField>

            <FormField label="Procedimento" htmlFor="procedimento">
              <Input
                id="procedimento"
                placeholder="Código ou nome"
                value={rascunho.procedimento}
                onChange={(event) => alterarRascunho('procedimento', event.target.value)}
              />
            </FormField>

            <FormField label="Observação" htmlFor="observacao">
              <Input
                id="observacao"
                placeholder="Observação"
                value={rascunho.observacao}
                onChange={(event) => alterarRascunho('observacao', event.target.value)}
              />
            </FormField>

            <FormField label="Cliente" htmlFor="clienteId" hint="Identificador na base">
              <Input
                id="clienteId"
                placeholder="Id do cliente"
                value={rascunho.clienteId}
                onChange={(event) => alterarRascunho('clienteId', event.target.value)}
              />
            </FormField>

            <FormField label="Operadora" htmlFor="operadoraId" hint="Identificador na base">
              <Input
                id="operadoraId"
                placeholder="Id da operadora"
                value={rascunho.operadoraId}
                onChange={(event) => alterarRascunho('operadoraId', event.target.value)}
              />
            </FormField>

            <FormField label="Credencial" htmlFor="credencialId" hint="Identificador na base">
              <Input
                id="credencialId"
                placeholder="Id da credencial"
                value={rascunho.credencialId}
                onChange={(event) => alterarRascunho('credencialId', event.target.value)}
              />
            </FormField>
          </>
        }
      />

      <DataTable
        columns={columns}
        page={page}
        getRowKey={(guia) => guia.id}
        isLoading={isFetching}
        error={error}
        onRetry={() => void refetch()}
        onRowClick={(guia) => navigate(`/autorizacoes/${guia.id}`)}
        onPageChange={setPageNumber}
        pageSize={pageSize}
        onPageSizeChange={(novoTamanho) => {
          setPageSize(novoTamanho)
          setPageNumber(1)
        }}
        emptyTitle="Nenhuma autorização encontrada"
        emptyDescription="Ajuste os filtros e tente novamente."
      />
    </>
  )
}

export default AutorizacoesList
